import { createTheme } from '@mui/material/styles';

// Modern professional color palette
const colors = {
  primary: {
    50: '#e8f4fd',
    100: '#c3e2f9',
    200: '#9acef5',
    300: '#6fb9f0',
    400: '#4fa9ed',
    500: '#2f98e9', // Primary brand color
    600: '#2a8bd4',
    700: '#1f73b7',
    800: '#165c9a',
    900: '#0a3d6b',
  },
  secondary: {
    50: '#f3f4f6',
    100: '#e5e7eb',
    200: '#d1d5db',
    300: '#9ca3af',
    400: '#6b7280',
    500: '#4b5563',
    600: '#374151',
    700: '#1f2937',
    800: '#111827',
    900: '#030712',
  },
  success: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    500: '#10b981',
    600: '#059669',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    500: '#f59e0b',
    600: '#d97706',
  },
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    500: '#ef4444',
    600: '#dc2626',
  },
};

const createModernTheme = (mode = 'dark') => {
  const isDark = mode === 'dark';
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary[500],
        light: colors.primary[300],
        dark: colors.primary[700],
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary[isDark ? 400 : 600],
        light: colors.secondary[isDark ? 300 : 400],
        dark: colors.secondary[isDark ? 600 : 800],
      },
      success: {
        main: colors.success[500],
        light: colors.success[300],
        dark: colors.success[600],
      },
      warning: {
        main: colors.warning[500],
        light: colors.warning[300],
        dark: colors.warning[600],
      },
      error: {
        main: colors.error[500],
        light: colors.error[300],
        dark: colors.error[600],
      },
      background: {
        default: isDark ? '#0f172a' : '#f8fafc',
        paper: isDark ? '#1e293b' : '#ffffff',
      },
      surface: {
        main: isDark ? '#334155' : '#f1f5f9',
        dark: isDark ? '#1e293b' : '#e2e8f0',
      },
      text: {
        primary: isDark ? '#f8fafc' : '#0f172a',
        secondary: isDark ? '#cbd5e1' : '#475569',
        disabled: isDark ? '#64748b' : '#94a3b8',
      },
      divider: isDark ? '#334155' : '#e2e8f0',
    },
    typography: {
      fontFamily: [
        'Inter',
        'Roboto',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontWeight: 800,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        letterSpacing: '-0.025em',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
        letterSpacing: '-0.025em',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        letterSpacing: '-0.025em',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: 1.4,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        letterSpacing: '0.00938em',
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
        letterSpacing: '0.01071em',
      },
      button: {
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0.02857em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: isDark ? [
      'none',
      '0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
      '0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
      '0px 1px 3px 0px rgba(0, 0, 0, 0.3), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
      '0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)',
      '0px 3px 4px 0px rgba(0, 0, 0, 0.3), 0px 8px 12px 6px rgba(0, 0, 0, 0.15)',
      '0px 3px 5px 0px rgba(0, 0, 0, 0.3), 0px 10px 14px 8px rgba(0, 0, 0, 0.15)',
      '0px 4px 5px 0px rgba(0, 0, 0, 0.3), 0px 12px 16px 10px rgba(0, 0, 0, 0.15)',
      '0px 5px 5px 0px rgba(0, 0, 0, 0.3), 0px 14px 18px 12px rgba(0, 0, 0, 0.15)',
      '0px 5px 6px 0px rgba(0, 0, 0, 0.3), 0px 16px 20px 14px rgba(0, 0, 0, 0.15)',
      '0px 6px 6px 0px rgba(0, 0, 0, 0.3), 0px 18px 22px 16px rgba(0, 0, 0, 0.15)',
      '0px 6px 7px 0px rgba(0, 0, 0, 0.3), 0px 20px 24px 18px rgba(0, 0, 0, 0.15)',
      '0px 7px 8px 0px rgba(0, 0, 0, 0.3), 0px 22px 26px 20px rgba(0, 0, 0, 0.15)',
      '0px 7px 8px 0px rgba(0, 0, 0, 0.3), 0px 24px 28px 22px rgba(0, 0, 0, 0.15)',
      '0px 7px 9px 0px rgba(0, 0, 0, 0.3), 0px 26px 30px 24px rgba(0, 0, 0, 0.15)',
      '0px 8px 9px 0px rgba(0, 0, 0, 0.3), 0px 28px 32px 26px rgba(0, 0, 0, 0.15)',
      '0px 8px 10px 0px rgba(0, 0, 0, 0.3), 0px 30px 34px 28px rgba(0, 0, 0, 0.15)',
      '0px 8px 11px 0px rgba(0, 0, 0, 0.3), 0px 32px 36px 30px rgba(0, 0, 0, 0.15)',
      '0px 9px 11px 0px rgba(0, 0, 0, 0.3), 0px 34px 38px 32px rgba(0, 0, 0, 0.15)',
      '0px 9px 12px 0px rgba(0, 0, 0, 0.3), 0px 36px 40px 34px rgba(0, 0, 0, 0.15)',
      '0px 10px 13px 0px rgba(0, 0, 0, 0.3), 0px 38px 42px 36px rgba(0, 0, 0, 0.15)',
      '0px 10px 13px 0px rgba(0, 0, 0, 0.3), 0px 40px 44px 38px rgba(0, 0, 0, 0.15)',
      '0px 10px 14px 0px rgba(0, 0, 0, 0.3), 0px 42px 46px 40px rgba(0, 0, 0, 0.15)',
      '0px 11px 14px 0px rgba(0, 0, 0, 0.3), 0px 44px 48px 42px rgba(0, 0, 0, 0.15)',
      '0px 11px 15px 0px rgba(0, 0, 0, 0.3), 0px 46px 50px 44px rgba(0, 0, 0, 0.15)'
    ] : undefined,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            boxSizing: 'border-box',
          },
          html: {
            MozOsxFontSmoothing: 'grayscale',
            WebkitFontSmoothing: 'antialiased',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
          },
          body: {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            minHeight: '100%',
            width: '100%',
          },
          '#root': {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
          },
          '#nprogress': {
            pointerEvents: 'none',
          },
          '#nprogress .bar': {
            background: colors.primary[500],
            height: 3,
            left: 0,
            position: 'fixed',
            top: 0,
            width: '100%',
            zIndex: 2000,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            boxShadow: isDark 
              ? '0px 1px 2px 0px rgba(0, 0, 0, 0.3)' 
              : '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            padding: '8px 16px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isDark
                ? '0px 4px 8px 0px rgba(0, 0, 0, 0.3)'
                : '0px 4px 8px 0px rgba(0, 0, 0, 0.12)',
            },
          },
          contained: {
            boxShadow: isDark
              ? '0px 2px 4px 0px rgba(0, 0, 0, 0.3)'
              : '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            boxShadow: isDark
              ? '0px 2px 8px 0px rgba(0, 0, 0, 0.25)'
              : '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: isDark
                ? '0px 8px 24px 0px rgba(0, 0, 0, 0.4)'
                : '0px 8px 24px 0px rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '& fieldset': {
                borderColor: isDark ? '#475569' : '#d1d5db',
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#64748b' : '#9ca3af',
              },
              '&.Mui-focused fieldset': {
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isDark
              ? '0px 4px 12px 0px rgba(0, 0, 0, 0.4)'
              : '0px 4px 12px 0px rgba(0, 0, 0, 0.15)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 12,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: isDark 
                ? 'rgba(148, 163, 184, 0.1)' 
                : 'rgba(100, 116, 139, 0.08)',
              transform: 'scale(1.05)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderTop: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            height: 70,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '4px',
            '&.Mui-selected': {
              backgroundColor: `${colors.primary[500]}20`,
              color: colors.primary[500],
            },
          },
        },
      },
    },
  });
};

export { createModernTheme, colors };
